import { createMiddleware } from "@tanstack/react-start";
import { setResponseHeader } from "@tanstack/react-start/server";
import {
  getTimingCollector,
  runWithTiming,
} from "#/modules/util.instrumentation/server-timing.server";

/**
 * Global REQUEST middleware that gives every server request (SSR document + server-function RPCs)
 * a request-scoped Server-Timing collector, then writes the collected backend-call spans to the
 * `Server-Timing` response header once the handler resolves. This is the TanStack port of portal's
 * remix-utils `createServerTimingMiddleware`.
 *
 * Because it is a request middleware (`createMiddleware().server(...)`), it wraps ALL server
 * requests — so each server-fn response carries the spans of the backend calls that RPC made
 * (portal grouped them under one navigation request; that per-RPC granularity is the intended
 * TanStack shape). The header is set via `setResponseHeader` from `@tanstack/react-start/server`,
 * the framework's sanctioned response-header API (re-exported from `@tanstack/start-server-core`).
 *
 * Timing must never break a request: serialising the collector and setting the header are guarded,
 * so any encoding error is swallowed and the original handler result is returned untouched.
 */
export const serverTimingMiddleware = createMiddleware().server(({ next }) =>
  runWithTiming(async () => {
    const result = await next();

    try {
      const value = getTimingCollector()?.toString();
      // Server-Timing rejects an empty value; only set it when spans were collected.
      if (value) {
        setResponseHeader("Server-Timing", value);
      }
    } catch {
      // Instrumentation is best-effort — never let it surface to the client.
    }

    return result;
  }),
);
